import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ExternalLink, RefreshCw, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PageState } from "@/components/common/PageState";
import { usePlatformPermissions } from "@/hooks/usePlatformPermissions";
import { formatDateTime } from "@/utils/dateUtils";
import type { PartnerSyncState } from "@/types/partner-settings";
import type {
  PartnerAiState,
  PartnerAttractionItem,
  PartnerAttractionRelatedProduct,
  PartnerDestinationAiStatus,
  PartnerDestinationAiTopListEntry,
  PartnerDestinationDetail,
  PartnerDestinationProduct,
  PartnerDestinationSyncResult,
  PartnerDestinationSyncStatus,
} from "@/types/partner-destination";
import { DestinationCatalogPanel } from "./components/DestinationCatalogPanel";
import { HotelPartnerSyncTable } from "./components/HotelPartnerSyncTable";
import { DestinationCuratedPanel } from "./components/DestinationCuratedPanel";
import { describeError } from "./utils/describeError";
import {
  CURATED_LIST_MAX_ITEMS,
  addToCurated,
  getCuratedLimitState,
} from "./utils/curatedLimit";
import {
  type CuratedSaveBlockReason,
  getCuratedSaveBlockReason,
  needsClearAllConfirmation,
  useAiStatus,
  useAllCuratedProducts,
  useDestinationAttractions,
  useRefreshDestinationAttractions,
  useCuratedList,
  useDestination,
  useDestinationSync,
  useRegenerateAi,
  useUpdateCuratedList,
} from "./hooks/partner-destinations.hooks";

const syncStateBadgeVariant = (
  state: PartnerSyncState,
): "secondary" | "success" | "destructive" | "outline" => {
  if (state === "succeeded") return "success";
  if (state === "failed") return "destructive";
  if (state === "partial") return "outline";
  return "secondary";
};

const aiStateBadgeVariant = (
  state: PartnerAiState,
): "secondary" | "success" | "destructive" | "outline" => {
  if (state === "succeeded") return "success";
  if (state === "failed") return "destructive";
  if (state === "running" || state === "queued") return "outline";
  return "secondary";
};

/**
 * What the sync-status block shows: the live poll while a "Sync" or "Save &
 * apply" job is in flight (the only thing updating every 5s), the
 * destination card's last-known fields at rest. Sim, 2026-09-28:
 * "succeeded" never appeared on screen because nothing here was live
 * during Queued/Running, and a toast was the only signal — and it hides
 * itself after a few seconds either way.
 */
function computeDisplaySyncStatus(params: {
  isSyncing: boolean;
  isApplying: boolean;
  syncLiveStatus: PartnerDestinationSyncStatus | null;
  applyLiveStatus: PartnerDestinationSyncStatus | null;
  destination: PartnerDestinationDetail;
}): {
  state: PartnerSyncState;
  lastSyncAt: string | null;
  lastResult: PartnerDestinationSyncResult | null;
  lastError: string | null;
} {
  let active: PartnerDestinationSyncStatus | null = null;
  if (params.isSyncing) {
    active = params.syncLiveStatus;
  } else if (params.isApplying) {
    active = params.applyLiveStatus;
  }

  return {
    state: active?.state ?? params.destination.syncState,
    lastSyncAt: active?.lastSyncAt ?? params.destination.lastSyncAt,
    lastResult: active?.lastResult ?? params.destination.lastResult,
    lastError: active?.lastError ?? params.destination.lastError,
  };
}

function formatDurationSeconds(durationMs: number): string {
  return `${(durationMs / 1000).toFixed(1)}s`;
}

/**
 * The found/not-found/found-but-excluded indicator for one AI top-list
 * entry (see `.ai.topListTitle`). A match can still be left out of the
 * final list (dedup, cap) — `inList` is what actually decides the badge,
 * `matchedProductCode` only decides whether it's a Viator match at all.
 */
function AiTopListEntryStatus({
  entry,
  t,
}: {
  entry: PartnerDestinationAiTopListEntry;
  t: TFunction;
}): React.JSX.Element {
  if (!entry.matchedProductCode) {
    return (
      <Badge variant="destructive" className="shrink-0">
        {t("partners.admin.destinations.ai.notFoundInViator")}
      </Badge>
    );
  }

  const badge = entry.inList ? (
    <Badge variant="success" className="shrink-0">
      {t("partners.admin.destinations.ai.foundInViator")}
    </Badge>
  ) : (
    <Badge variant="outline" className="shrink-0">
      {t("partners.admin.destinations.ai.foundNotInList")}
    </Badge>
  );

  if (entry.source_url) {
    return (
      <a
        href={entry.source_url}
        target="_blank"
        rel="noreferrer"
        className="flex shrink-0 items-center gap-1 hover:underline"
      >
        <ExternalLink className="size-3" />
        {badge}
      </a>
    );
  }

  return badge;
}

/**
 * "Regenerate with AI" confirmation — a real `Dialog` rather than
 * `window.confirm` (used elsewhere on this page for plain yes/no actions)
 * because the backend contract needs a `keepManual` choice, defaulted on.
 */
function RegenerateAiDialog({
  t,
  open,
  onOpenChange,
  onConfirm,
}: {
  t: TFunction;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (keepManual: boolean) => void;
}): React.JSX.Element {
  const [keepManual, setKeepManual] = useState(true);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) setKeepManual(true);
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogTitle>
          {t("partners.admin.destinations.ai.regenerateDialogTitle")}
        </DialogTitle>
        <DialogDescription>
          {t("partners.admin.destinations.ai.regenerateDialogDescription")}
        </DialogDescription>

        <div className="flex items-center gap-2 py-2">
          <Checkbox
            id="ai-regenerate-keep-manual"
            checked={keepManual}
            onCheckedChange={(checked) => setKeepManual(checked === true)}
          />
          <Label htmlFor="ai-regenerate-keep-manual">
            {t("partners.admin.destinations.ai.keepManualLabel")}
          </Label>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={() => {
              onConfirm(keepManual);
              onOpenChange(false);
            }}
          >
            {t("partners.admin.destinations.ai.regenerateDialogConfirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * "AI curation" card (task 0027): status, last run, error, counts and the
 * AI's top-20 for the city, plus the "Regenerate with AI" action. Pulled out
 * of the page component so its own conditionals don't add to the page's
 * cognitive complexity — same reasoning as `renderCuratedSection` above.
 */
function AiCurationCard({
  t,
  canWrite,
  isAiBusy,
  onOpenRegenerateDialog,
  isLoading,
  isError,
  error,
  aiStatus,
  cityName,
}: {
  t: TFunction;
  canWrite: boolean;
  isAiBusy: boolean;
  onOpenRegenerateDialog: () => void;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  aiStatus: PartnerDestinationAiStatus | undefined;
  cityName: string;
}): React.JSX.Element {
  const topList = aiStatus?.topList ?? null;

  return (
    <div className="flex flex-col gap-3 rounded-xl border p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-foreground text-lg font-semibold">
          {t("partners.admin.destinations.ai.title")}
        </h2>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!canWrite || isAiBusy}
          title={
            canWrite
              ? undefined
              : t("common.insufficientPermissions", "Insufficient permissions")
          }
          onClick={onOpenRegenerateDialog}
        >
          <RefreshCw className={isAiBusy ? "size-4 animate-spin" : "size-4"} />
          {isAiBusy
            ? t("partners.admin.destinations.ai.regenerating")
            : t("partners.admin.destinations.ai.regenerate")}
        </Button>
      </div>

      {isLoading && (
        <p className="text-muted-foreground py-4 text-center text-sm">
          {t("partners.admin.destinations.loading")}
        </p>
      )}

      {isError && (
        <p className="text-destructive py-4 text-center text-sm">
          {describeError(
            t("partners.admin.destinations.ai.statusLoadError"),
            error,
          )}
        </p>
      )}

      {aiStatus && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">
              {t("partners.admin.destinations.ai.status")}
            </span>
            <Badge variant={aiStateBadgeVariant(aiStatus.aiState)}>
              {t(
                `partners.admin.destinations.aiStateValues.${aiStatus.aiState}`,
                { defaultValue: aiStatus.aiState },
              )}
            </Badge>
            <span className="text-muted-foreground text-xs">
              {t("partners.admin.destinations.ai.lastRun")}:{" "}
              {aiStatus.aiRunAt
                ? formatDateTime(aiStatus.aiRunAt)
                : t("partners.admin.destinations.ai.lastRunNever")}
            </span>
          </div>

          {aiStatus.aiState === "failed" && aiStatus.aiError && (
            <p className="text-destructive text-xs">
              {t("partners.admin.destinations.ai.error", {
                error: aiStatus.aiError,
              })}
            </p>
          )}

          <p className="text-muted-foreground text-xs">
            {t("partners.admin.destinations.ai.counts", aiStatus.counts)}
          </p>

          {aiStatus.lastResult && (
            <p className="text-muted-foreground text-xs">
              {t("partners.admin.destinations.ai.usageLabel", {
                // Sim, 2026-09-28: when a field is missing from the
                // response (e.g. `webSearches` on a model-knowledge-only
                // run), i18next leaves the raw `{{placeholder}}` on screen
                // instead of substituting it — default every field to 0 so
                // the line always renders as numbers.
                calls: aiStatus.lastResult.usage?.calls ?? 0,
                inputTokens: aiStatus.lastResult.usage?.inputTokens ?? 0,
                outputTokens: aiStatus.lastResult.usage?.outputTokens ?? 0,
                webSearches: aiStatus.lastResult.usage?.webSearches ?? 0,
                duration: formatDurationSeconds(aiStatus.lastResult.durationMs),
              })}
            </p>
          )}

          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-foreground text-sm font-semibold">
                {t("partners.admin.destinations.ai.topListTitle", {
                  city: cityName,
                })}
              </h3>
              {topList && (
                <span className="text-muted-foreground text-xs">
                  {t("partners.admin.destinations.ai.sourcesModeLabel", {
                    mode: t(
                      `partners.admin.destinations.ai.sourcesModeValues.${topList.sourcesMode}`,
                    ),
                  })}
                </span>
              )}
            </div>

            {topList?.webSearchError && (
              <p className="text-destructive text-xs">
                {t("partners.admin.destinations.ai.webSearchErrorLabel", {
                  error: topList.webSearchError,
                })}
              </p>
            )}

            {!topList || topList.items.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {t("partners.admin.destinations.ai.topListEmpty")}
              </p>
            ) : (
              <ul className="flex flex-col gap-1">
                {topList.items.map((entry, index) => (
                  <li
                    key={`${entry.matchedProductCode ?? entry.title}-${index}`}
                    className="flex items-center justify-between gap-2 text-sm"
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="truncate">{entry.title}</span>
                      </TooltipTrigger>
                      <TooltipContent>{entry.why}</TooltipContent>
                    </Tooltip>
                    <AiTopListEntryStatus entry={entry} t={t} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Up to 3 bookable activities related to an attraction (task 0032, contract
 * 2026-09-29) — each flagged if it's already on the city's curated list.
 */
function AttractionRelatedProducts({
  products,
  t,
}: {
  products: PartnerAttractionRelatedProduct[];
  t: TFunction;
}): React.JSX.Element | null {
  if (products.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground text-xs font-medium">
        {t("partners.admin.destinations.attractions.relatedProductsTitle")}
      </span>
      <ul className="flex flex-col gap-1">
        {products.map((product) => (
          <li
            key={product.productCode}
            className="flex items-center justify-between gap-2 text-xs"
          >
            <span className="truncate">{product.title}</span>
            {product.onCityList && (
              <Badge variant="success" className="shrink-0">
                {t("partners.admin.destinations.attractions.onCityList")}
              </Badge>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** One attraction card — provider or AI-sourced, task 0032. */
function AttractionCard({
  item,
  t,
}: {
  item: PartnerAttractionItem;
  t: TFunction;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2 rounded-xl border p-4">
      <div className="flex items-start gap-3">
        {item.imageUrl && (
          <img
            src={item.imageUrl}
            alt=""
            className="size-16 shrink-0 rounded-lg object-cover"
          />
        )}
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-foreground text-sm font-semibold">
              {item.title}
            </h3>
            <Badge variant="outline">
              {t(
                `partners.admin.destinations.attractions.sourceValues.${item.source}`,
              )}
            </Badge>
            {item.aiLandmark && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="secondary" className="cursor-help">
                    {t("partners.admin.destinations.attractions.aiRankLabel", {
                      rank: item.aiLandmark.rank,
                    })}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  {t("partners.admin.destinations.attractions.aiWhyLabel", {
                    why: item.aiLandmark.why,
                  })}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <span className="text-muted-foreground text-xs">
            {item.rating != null
              ? t("partners.admin.destinations.attractions.rating", {
                  rating: item.rating,
                  count: item.reviewCount ?? 0,
                })
              : t("partners.admin.destinations.attractions.noRating")}
          </span>
          {item.description && (
            <p className="text-muted-foreground text-xs">{item.description}</p>
          )}
          {item.url && (
            <a
              href={item.url}
              target="_blank"
              rel="noreferrer"
              className="text-primary flex w-fit items-center gap-1 text-xs hover:underline"
            >
              <ExternalLink className="size-3" />
              {t("partners.admin.destinations.attractions.viewLink")}
            </a>
          )}
        </div>
      </div>
      <AttractionRelatedProducts products={item.relatedProducts} t={t} />
    </div>
  );
}

/**
 * "Attractions" tab (task 0032): status, last refresh, sources mode,
 * Refresh (polls to completion the same way `AiCurationCard` does), and the
 * attraction cards. SUPER_ADMIN/GUESTERIA_ADMIN only — the route/page
 * guard above this component already enforces that; this tab adds nothing
 * further.
 */
function AttractionsTabContent({
  t,
  canWrite,
  destinationId,
}: {
  t: TFunction;
  canWrite: boolean;
  destinationId: string;
}): React.JSX.Element {
  const { data, isLoading, isError, error } =
    useDestinationAttractions(destinationId);
  const { refresh, isRefreshing } =
    useRefreshDestinationAttractions(destinationId);
  const isBusy =
    isRefreshing || data?.state === "queued" || data?.state === "running";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {data && (
            <>
              <Badge variant={aiStateBadgeVariant(data.state)}>
                {t(`partners.admin.destinations.aiStateValues.${data.state}`, {
                  defaultValue: data.state,
                })}
              </Badge>
              <span className="text-muted-foreground text-xs">
                {t("partners.admin.destinations.attractions.lastRefresh")}:{" "}
                {data.refreshedAt
                  ? formatDateTime(data.refreshedAt)
                  : t(
                      "partners.admin.destinations.attractions.lastRefreshNever",
                    )}
              </span>
              {data.sourcesMode && (
                <span className="text-muted-foreground text-xs">
                  {t(
                    "partners.admin.destinations.attractions.sourcesModeLabel",
                    {
                      mode: t(
                        `partners.admin.destinations.ai.sourcesModeValues.${data.sourcesMode}`,
                      ),
                    },
                  )}
                </span>
              )}
            </>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!canWrite || isBusy}
          onClick={() => refresh()}
        >
          <RefreshCw className={isBusy ? "size-4 animate-spin" : "size-4"} />
          {isBusy
            ? t("partners.admin.destinations.attractions.refreshing")
            : t("partners.admin.destinations.attractions.refresh")}
        </Button>
      </div>

      {data?.state === "failed" && data.error && (
        <p className="text-destructive text-xs">
          {t("partners.admin.destinations.attractions.error", {
            error: data.error,
          })}
        </p>
      )}

      {isLoading && (
        <p className="text-muted-foreground py-4 text-center text-sm">
          {t("partners.admin.destinations.loading")}
        </p>
      )}

      {isError && (
        <p className="text-destructive py-4 text-center text-sm">
          {describeError(
            t("partners.admin.destinations.attractions.statusLoadError"),
            error,
          )}
        </p>
      )}

      {data && data.items.length === 0 && (
        <p className="text-muted-foreground text-sm">
          {t("partners.admin.destinations.attractions.empty")}
        </p>
      )}

      {data && data.items.length > 0 && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {data.items.map((item, index) => (
            <AttractionCard
              key={item.productCode ?? `${item.title}-${index}`}
              item={item}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function getBlockReasonText(
  t: TFunction,
  canWrite: boolean,
  blockReason: CuratedSaveBlockReason | "overLimit",
  extra = 0,
): string | null {
  if (!canWrite) {
    return t("common.insufficientPermissions", "Insufficient permissions");
  }
  if (blockReason === "notLoaded" || blockReason === "loadError") {
    return t("partners.admin.destinations.detail.blockedLoading");
  }
  if (blockReason === "emptyEnabled") {
    return t("partners.admin.destinations.detail.blockedEmptyEnabled");
  }
  if (blockReason === "overLimit") {
    return t("partners.admin.destinations.detail.blockedOverLimit", {
      extra,
      max: CURATED_LIST_MAX_ITEMS,
    });
  }
  return null;
}

/**
 * The curated (right) panel's own loading/error state — deliberately
 * independent of the catalog (left) panel, which manages itself inside
 * `DestinationCatalogPanel`. Coupling the two was the root of the
 * 2026-09-28 data-loss defect: a failed catalog fetch blanked the curated
 * panel too, and the (disabled) Save button gave no visible reason why.
 */
function renderCuratedSection({
  isLoaded,
  isLoadError,
  statusText,
  items,
  onReorder,
  onRemove,
  canWrite,
}: {
  isLoaded: boolean;
  isLoadError: boolean;
  /** Either the "loading" or the load-error text, chosen by the caller. */
  statusText: string;
  items: PartnerDestinationProduct[];
  onReorder: (items: PartnerDestinationProduct[]) => void;
  onRemove: (productCode: string) => void;
  canWrite: boolean;
}): React.JSX.Element {
  if (isLoadError) {
    return (
      <p className="text-destructive py-8 text-center text-sm">{statusText}</p>
    );
  }

  if (!isLoaded) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        {statusText}
      </p>
    );
  }

  return (
    <DestinationCuratedPanel
      items={items}
      onReorder={onReorder}
      onRemove={onRemove}
      canWrite={canWrite}
    />
  );
}

export const DestinationDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { destinationId } = useParams<{ destinationId: string }>();
  const { canAdminister } = usePlatformPermissions();
  const canWrite = canAdminister("partners");

  const {
    data: destination,
    isLoading: isDestinationLoading,
    isError: isDestinationError,
    error: destinationError,
  } = useDestination(destinationId);

  const {
    data: curatedList,
    isError: isCuratedListError,
    error: curatedListError,
  } = useCuratedList(destinationId);

  const {
    data: curatedProducts,
    isError: isCuratedProductsError,
    error: curatedProductsError,
  } = useAllCuratedProducts(destinationId);

  // Resume polling if the card already reports a job in flight — e.g. the
  // page was (re)loaded while a previously-triggered sync was still running.
  const resumeSyncId =
    destination &&
    (destination.syncState === "queued" || destination.syncState === "running")
      ? destination.syncId
      : undefined;

  const {
    isSyncing,
    triggerSync,
    liveStatus: syncLiveStatus,
  } = useDestinationSync(destinationId, { resumeSyncId });

  const {
    data: aiStatus,
    isLoading: isAiStatusLoading,
    isError: isAiStatusError,
    error: aiStatusError,
  } = useAiStatus(destinationId);
  const { regenerate, isRegenerating } = useRegenerateAi(destinationId);
  const isAiBusy =
    isRegenerating ||
    aiStatus?.aiState === "queued" ||
    aiStatus?.aiState === "running";
  const [isRegenerateDialogOpen, setIsRegenerateDialogOpen] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [items, setItems] = useState<PartnerDestinationProduct[]>([]);
  const [pendingAction, setPendingAction] = useState<"save" | "apply" | null>(
    null,
  );

  // Seed local editable state once from the server, then let the user's
  // edits own it — a background refetch must not clobber unsaved changes.
  // Both sources must succeed: `curatedList` for `enabled`, `curatedProducts`
  // for the enriched cards `items` is built from.
  const [isSeeded, setIsSeeded] = useState(false);
  const seededItemsCountRef = useRef(0);
  useEffect(() => {
    if (isSeeded || !curatedList || !curatedProducts) return;
    setEnabled(curatedList.enabled);
    const sorted = [...curatedProducts].sort(
      (a, b) => (a.position ?? 0) - (b.position ?? 0),
    );
    setItems(sorted);
    seededItemsCountRef.current = sorted.length;
    setIsSeeded(true);
  }, [isSeeded, curatedList, curatedProducts]);

  const isCuratedError = isCuratedListError || isCuratedProductsError;

  const curatedProductCodes = useMemo(
    () => new Set(items.map((item) => item.productCode)),
    [items],
  );

  const {
    saveCuratedList,
    isSaving,
    isApplying,
    liveStatus: applyLiveStatus,
  } = useUpdateCuratedList(destinationId, {
    onSyncCompleted: () => setPendingAction(null),
  });

  const limit = getCuratedLimitState(items.length);

  const handleAdd = (product: PartnerDestinationProduct) => {
    if (curatedProductCodes.has(product.productCode) || !limit.canAdd) return;
    // addToCurated re-checks against the latest list: two quick clicks
    // cannot push it past the limit.
    setItems((current) => addToCurated(current, product));
  };

  const handleRemove = (productCode: string) => {
    setItems((current) =>
      current.filter((item) => item.productCode !== productCode),
    );
  };

  const blockReason =
    getCuratedSaveBlockReason({
      isLoaded: isSeeded,
      isLoadError: isCuratedError,
      enabled,
      itemsCount: items.length,
    }) ?? (limit.isOver ? "overLimit" : null);

  const blockReasonText = getBlockReasonText(
    t,
    canWrite,
    blockReason,
    limit.extra,
  );

  const handleSave = async (action: "save" | "apply") => {
    if (!destinationId || blockReason !== null) return;

    if (needsClearAllConfirmation(items.length, seededItemsCountRef.current)) {
      const confirmed = window.confirm(
        t("partners.admin.destinations.detail.confirmClearAll", {
          count: seededItemsCountRef.current,
        }),
      );
      if (!confirmed) return;
    }

    setPendingAction(action);
    try {
      await saveCuratedList({
        enabled,
        items: items.map((item, index) => ({
          productCode: item.productCode,
          position: index,
        })),
      });
      toast.success(t("partners.admin.destinations.detail.saveSuccess"));
      seededItemsCountRef.current = items.length;
      if (action === "save") setPendingAction(null);
    } catch {
      // useUpdateCuratedList's onError already shows the failure toast.
      setPendingAction(null);
    }
  };

  if (isDestinationLoading) {
    return <PageState text={t("partners.admin.destinations.loading")} />;
  }

  if (isDestinationError || !destination) {
    return (
      <PageState
        text={describeError(
          t("partners.admin.destinations.detail.loadError"),
          destinationError,
        )}
        tone="error"
      />
    );
  }

  const isSaveDisabled =
    !canWrite || isSaving || isApplying || blockReason !== null;

  const {
    state: displayState,
    lastSyncAt: displayLastSyncAt,
    lastResult: displayLastResult,
    lastError: displayLastError,
  } = computeDisplaySyncStatus({
    isSyncing,
    isApplying,
    syncLiveStatus,
    applyLiveStatus,
    destination,
  });

  return (
    <div className="mx-auto flex flex-col gap-6 p-12">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="w-fit"
        onClick={() => navigate("/partners/destinations")}
      >
        <Undo2 className="size-4" />
        {t("partners.admin.destinations.detail.back")}
      </Button>

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-foreground text-2xl font-semibold">
            {destination.name}
          </h1>
          <Badge variant={syncStateBadgeVariant(displayState)}>
            {t(`partners.admin.destinations.syncStateValues.${displayState}`, {
              defaultValue: displayState,
            })}
          </Badge>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-muted-foreground text-sm">
            {displayLastSyncAt
              ? formatDateTime(displayLastSyncAt)
              : t("partners.admin.destinations.neverSynced")}
          </span>
          <Button
            type="button"
            variant="outline"
            disabled={!canWrite || isSyncing}
            onClick={() => triggerSync()}
          >
            <RefreshCw
              className={isSyncing ? "size-4 animate-spin" : "size-4"}
            />
            {isSyncing
              ? t("partners.admin.destinations.syncing")
              : t("partners.admin.destinations.sync")}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="activities">
        <TabsList>
          <TabsTrigger value="activities">
            {t("partners.admin.destinations.detail.tabs.activities")}
          </TabsTrigger>
          <TabsTrigger value="attractions">
            {t("partners.admin.destinations.detail.tabs.attractions")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="activities" className="flex flex-col gap-6">
          <div className="flex items-center gap-3 rounded-xl border p-4">
            <Switch
              id="curated-list-enabled"
              checked={enabled}
              disabled={!canWrite || !isSeeded}
              onCheckedChange={setEnabled}
            />
            <div className="flex flex-col">
              <Label htmlFor="curated-list-enabled">
                {t("partners.admin.destinations.detail.curatedToggle")}
              </Label>
              <span className="text-muted-foreground text-xs">
                {t("partners.admin.destinations.detail.curatedToggleHint")}
              </span>
            </div>
          </div>

          <AiCurationCard
            t={t}
            canWrite={canWrite}
            isAiBusy={isAiBusy}
            onOpenRegenerateDialog={() => setIsRegenerateDialogOpen(true)}
            isLoading={isAiStatusLoading}
            isError={isAiStatusError}
            error={aiStatusError}
            aiStatus={aiStatus}
            cityName={destination.name}
          />
          <RegenerateAiDialog
            t={t}
            open={isRegenerateDialogOpen}
            onOpenChange={setIsRegenerateDialogOpen}
            onConfirm={(keepManual) => regenerate({ keepManual })}
          />

          <div className="flex flex-col gap-2">
            <h2 className="text-foreground text-lg font-semibold">
              {t("partners.admin.destinations.detail.hotelsTitle")}
            </h2>
            <HotelPartnerSyncTable
              t={t}
              hotels={destination.hotels}
              canWrite={canWrite}
            />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {destinationId && (
              <DestinationCatalogPanel
                destinationId={destinationId}
                curatedProductCodes={curatedProductCodes}
                onAdd={handleAdd}
                isCuratedFull={!limit.canAdd}
                canWrite={canWrite}
                isSyncing={isSyncing}
                onTriggerSync={triggerSync}
              />
            )}
            {renderCuratedSection({
              isLoaded: isSeeded,
              isLoadError: isCuratedError,
              statusText: isCuratedError
                ? describeError(
                    t(
                      "partners.admin.destinations.detail.curatedListLoadError",
                    ),
                    curatedListError ?? curatedProductsError,
                  )
                : t("partners.admin.destinations.loading"),
              items,
              onReorder: setItems,
              onRemove: handleRemove,
              canWrite,
            })}
          </div>

          {/* Always rendered — never hidden after a sync/apply job finishes
          (sim, 2026-09-28: "succeeded" was invisible on screen). Live
          Queued/Running while a job is in flight, last-known state at rest. */}
          <div className="flex flex-col gap-1 rounded-xl border p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">
                {t("partners.admin.destinations.detail.syncStatusLabel")}
              </span>
              <Badge variant={syncStateBadgeVariant(displayState)}>
                {t(
                  `partners.admin.destinations.syncStateValues.${displayState}`,
                  {
                    defaultValue: displayState,
                  },
                )}
              </Badge>
              <span className="text-muted-foreground text-xs">
                {displayLastSyncAt
                  ? formatDateTime(displayLastSyncAt)
                  : t("partners.admin.destinations.neverSynced")}
              </span>
            </div>

            {displayLastResult && (
              <p className="text-muted-foreground text-xs">
                {t("partners.admin.destinations.detail.syncSummaryShort", {
                  hotelsMaterialized: displayLastResult.hotelsMaterialized,
                  cached: displayLastResult.cached,
                })}
              </p>
            )}

            {displayState === "failed" && displayLastError && (
              <p className="text-destructive text-xs">
                {t("partners.admin.destinations.detail.lastSyncError", {
                  error: displayLastError,
                })}
              </p>
            )}
          </div>

          <div className="flex flex-col items-end gap-2 border-t pt-4">
            {blockReasonText && (
              <p className="text-muted-foreground text-xs">{blockReasonText}</p>
            )}
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                disabled={isSaveDisabled}
                onClick={() => handleSave("save")}
              >
                {pendingAction === "save" && isSaving
                  ? t("partners.admin.destinations.detail.saving")
                  : t("partners.admin.destinations.detail.save")}
              </Button>
              <Button
                type="button"
                disabled={isSaveDisabled}
                onClick={() => handleSave("apply")}
              >
                {pendingAction === "apply" && (isSaving || isApplying)
                  ? t("partners.admin.destinations.detail.applying")
                  : t("partners.admin.destinations.detail.saveAndApply")}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="attractions">
          {destinationId && (
            <AttractionsTabContent
              t={t}
              canWrite={canWrite}
              destinationId={destinationId}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
